import { useApolloClient } from '@apollo/client';
import {
  LayoutGridContainer,
  LayoutGridItem,
  LayoutVerticalSpacer,
  SearchResultsList,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import Error from 'next/error';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

// TODO (WEB-1549) Remove after Design System update
Link.defaultProps = {
  legacyBehavior: true,
};

import { useRouterChangeLoader } from '~/common/utils/hooks';
import { useAuthenticationContext } from '~/components/AuthenticationProvider/useAuthenticationContext';
import {
  SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID,
  SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID_VALUES,
  SNIFFER_API_PHASE_2_EPPO_ID,
  SNIFFER_API_PHASE_2_EPPO_ID_VALUES,
} from '~/components/Eppo/constants';
import { useEppoStringAssignment } from '~/components/Eppo/helpers';
import { AdvancedSearch } from '~/components/SearchFiltersForm/AdvancedSearch/AdvancedSearch';
import { trackEvent } from '~/components/analytics/analytics';
import {
  CLICKED_SITTER_CARD_EVENT_NAME,
  SEARCHED_FOR_SITTER_EVENT_NAME,
  SitterFavouriteSourcesEnum,
  SITTER_FAVOURITED_CLICK,
  SITTER_UNFAVOURITE_CLICK,
  UPDATE_SITTER_SEARCH_EVENT_NAME,
} from '~/components/analytics/constants';
import { prepareUrl } from '~/components/utils/search';
import { getSearchFilterBookingType } from '~/components/utils/searchFilters';
import { SitterApi } from '~/services/sitter/SitterApi';

import { AggregatedRatingWidget } from './AggregatedRatingWidget/AggregatedRatingWidget';
import { DynamicSeoContent } from './DynamicSeoContent/DynamicSeoContent';
import { PageHead } from './PageHead/PageHead';
import { useUpdateContextualLinks } from './hooks/useUpdateContextualLinks/useUpdateContextualLinks';
import { GET_SEARCH_PRICE } from './queries';
import { getEventProperties as getEventPropertiesForUpdatedSearch } from './tracking/getUpdatedSearchTrackingProperties';
import {
  detectCrawlerBeforeEventTrigger,
  getPageHrefTemplate,
  getSearchResultsItems,
  getSearchResultsParameters,
  getServiceDetailsForSearch,
  preparePriceQueryVariables,
  shouldShowPriceFromPreview,
} from './utils';

import type { SitterPrice } from './utils';
import type { LinkComponent } from '@madpaws/design-system';
import type { NextPage } from 'next';
import type {
  SearchResults as ISearchResults,
  SearchFilters,
  SearchLink,
  SearchResultPrice,
} from '~/common/types/search';

export const DEFAULT_SEARCH_PAGE_META =
  'Join Mad Paws and discover local, trusted and insured Pet Sitting and Pet Boarding Services';
export const DEFAULT_SEARCH_PAGE_TOTAL_SITTERS = 0;

type Props = {
  canonicalUrl: string | null;
  likedSitterIds: number[];
  pageSlug: string;
  pageTitle: string;
  relatedSearchesLinks: SearchLink[] | null;
  searchFilters: SearchFilters;
  searchResults: ISearchResults;
  serviceSlug: string;
  statusCode?: number;
};

const SearchResults: NextPage<Props> = ({
  canonicalUrl,
  pageSlug,
  pageTitle,
  relatedSearchesLinks,
  searchFilters: serverSideSearchFilters,
  searchResults,
  serviceSlug,
  statusCode,
  likedSitterIds,
}) => {
  const isSetWeeklyAsTheDefaultOptionForRWB =
    useEppoStringAssignment(SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID) ===
    SET_WEEKLY_AS_THE_DEFAULT_OPTION_FOR_RWB_EPPO_ID_VALUES.ON;
  const isSnifferAPIPhase2Enabled =
    useEppoStringAssignment(SNIFFER_API_PHASE_2_EPPO_ID) === SNIFFER_API_PHASE_2_EPPO_ID_VALUES.ON;

  const searchFilters = useMemo(
    () =>
      isSetWeeklyAsTheDefaultOptionForRWB
        ? {
            ...serverSideSearchFilters,
            bookingType: getSearchFilterBookingType(serverSideSearchFilters),
          }
        : serverSideSearchFilters,
    [isSetWeeklyAsTheDefaultOptionForRWB, serverSideSearchFilters]
  );

  const router = useRouter();
  const isViewingSnifferPhase2Page = router.query?.isSnifferPhase2 === 'true';
  const isFirstPageLoading = useRef<boolean>(true);
  const isLoading = useRouterChangeLoader(false);

  const [likedSitters, setLikedSitters] = useState<number[]>(likedSitterIds);

  const { checkLoginAndOpenAuthDialog, isUserDetailsLoading } = useAuthenticationContext();

  // ref to mark that prices preview data
  // have been fetched and mapped to prices state
  const hasPricesPreview = useRef<boolean>(true);
  const serviceDetails = getServiceDetailsForSearch(searchFilters);
  const showPriceFromPreview =
    shouldShowPriceFromPreview(searchFilters, serviceDetails.id) && hasPricesPreview.current;

  const isMediumFromViewport = useMediumFromMediaQuery();

  const [prices, setPrices] = useState<SitterPrice[]>([]);

  // we don't use total price for now
  const hasTotalPrice = false;

  const {
    aggregateRating,
    dynamicSearchContent,
    metaDescription = DEFAULT_SEARCH_PAGE_META,
    nearSuburbs: nearbySuburbsLinks,
    serviceType,
  } = searchResults.metaInfo;

  const sittersCount = searchResults.pageInfo.total || DEFAULT_SEARCH_PAGE_TOTAL_SITTERS;

  useUpdateContextualLinks(nearbySuburbsLinks, relatedSearchesLinks, searchFilters, serviceSlug);

  const client = useApolloClient();

  const fetchPrice = (id: number): void => {
    const variables = preparePriceQueryVariables(id, searchFilters);
    (async (): Promise<void> => {
      const {
        data: { searchResultPrice },
      } = await client.query<{ searchResultPrice: SearchResultPrice }>({
        query: GET_SEARCH_PRICE,
        variables,
        fetchPolicy: 'network-only',
      });

      if (searchResultPrice === null) {
        const basePrices = searchResults.results.map((result) => ({
          id: result.id,
          price: result.basePrice,
        }));
        setPrices(basePrices);
        hasPricesPreview.current = false;
        return;
      }

      const price = searchResultPrice.preview.basePrice.value;

      setPrices((oldPrices) => [
        ...oldPrices,
        {
          id,
          price,
        },
      ]);

      hasPricesPreview.current = true;
    })();
  };

  useEffect(() => {
    if (!isFirstPageLoading.current) {
      return;
    }

    detectCrawlerBeforeEventTrigger(() =>
      trackEvent(
        SEARCHED_FOR_SITTER_EVENT_NAME,
        getEventPropertiesForUpdatedSearch(searchFilters, searchResults)
      )
    );

    isFirstPageLoading.current = false;
  }, [searchFilters, searchResults]);

  const pageHrefTemplate = getPageHrefTemplate(pageSlug);

  const handleFiltersSave = (newSearchFilters: SearchFilters): void => {
    const eventProperties = getEventPropertiesForUpdatedSearch(newSearchFilters);

    trackEvent(UPDATE_SITTER_SEARCH_EVENT_NAME, eventProperties);

    const url = prepareUrl(newSearchFilters, isSnifferAPIPhase2Enabled);
    router.push(url);

    setPrices([]);
  };

  const handleSitterOnSelect = (index: number): void => {
    const { offset, size } = searchResults.pageInfo;
    const eventProperties = getEventPropertiesForUpdatedSearch(searchFilters);
    const pageNumber = offset / size + 1;
    const sitterPosition = offset + index + 1;

    trackEvent(CLICKED_SITTER_CARD_EVENT_NAME, {
      ...eventProperties,
      sitter_position: sitterPosition,
      page_number: pageNumber,
      review_present: searchResults.results[index].latestReview !== null,
    });
  };

  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }

  const { stateName, suburb } = searchFilters.location.address;

  const handleClickLike = (id: number, isAddFavourite: boolean, index?: number): void => {
    checkLoginAndOpenAuthDialog(
      async () => {
        const sitter = searchResults.results[index as number];
        const eventName = isAddFavourite ? SITTER_FAVOURITED_CLICK : SITTER_UNFAVOURITE_CLICK;

        trackEvent(eventName, {
          sitterId: id,
          source: SitterFavouriteSourcesEnum.SFS_SITTER_CARD,
          sitter_name: sitter.name,
          sitter_photo: sitter.profilePhoto,
          sitter_profile_url: sitter.profileUrl,
        });

        setLikedSitters((oldLikedSitters: number[]) => {
          if (isAddFavourite) {
            return [...oldLikedSitters, id];
          }

          return oldLikedSitters.filter((sitterId) => sitterId !== id);
        });

        const response = await SitterApi.updateFavoriteSitter(id, isAddFavourite);

        if (!response) {
          // Revert the like state in case of error
          setLikedSitters((oldLikedSitters: number[]) => {
            if (isAddFavourite) {
              return oldLikedSitters.filter((sitterId) => sitterId !== id);
            }

            return [...oldLikedSitters, id];
          });
        }
      },
      'Create an account or log in to save your favourite sitters',
      router.asPath
    );
  };

  return (
    <>
      <PageHead
        canonicalUrl={canonicalUrl}
        metaDescription={metaDescription || DEFAULT_SEARCH_PAGE_META}
        pageTitle={pageTitle}
        seoStructuredData={{
          ...aggregateRating,
          serviceType,
          stateName,
          suburb,
          url: pageSlug,
        }}
      />
      <LayoutVerticalSpacer space={isMediumFromViewport ? 'large2x' : 'large1x'}>
        <AdvancedSearch
          currentValues={searchFilters}
          isLoading={isLoading}
          onFilterSubmit={handleFiltersSave}
          serviceSlug={serviceSlug}
          sittersCount={sittersCount}
        />
      </LayoutVerticalSpacer>
      <LayoutGridContainer>
        <LayoutGridItem placement={{ small: [2, 12], medium: [3, 11], large: [4, 10] }}>
          <LayoutVerticalSpacer space={isMediumFromViewport ? 'large3x' : 'large2x'}>
            <div data-nosnippet>
              <SearchResultsList
                // TODO (WEB-86): remove emptyStateCtaOnClick property and
                // Refine search button from empty state
                emptyStateCtaOnClick={(): void => undefined}
                hasLikedButton
                hasTotalPrice={hasTotalPrice}
                isLoadingItems={isLoading || (showPriceFromPreview && isUserDetailsLoading)}
                isSnifferAPISearchEnabled={isViewingSnifferPhase2Page}
                isTaglineHidden={!isMediumFromViewport}
                items={getSearchResultsItems(
                  searchResults,
                  searchFilters,
                  prices,
                  showPriceFromPreview,
                  true,
                  likedSitters
                )}
                linkComponent={Link as LinkComponent}
                onClickLike={handleClickLike}
                onClickSearchResultCard={handleSitterOnSelect}
                onLoadPrice={(id: number): void => fetchPrice(id)}
                pageHrefTemplate={pageHrefTemplate}
                pageInfo={searchResults.pageInfo}
                searchParameters={getSearchResultsParameters(searchFilters, serviceDetails)}
              />
            </div>
          </LayoutVerticalSpacer>
        </LayoutGridItem>
        <LayoutGridItem placement={{ small: [2, 12], medium: [3, 11], large: [4, 10] }}>
          {sittersCount > 0 && (
            <>
              {aggregateRating && (
                <LayoutVerticalSpacer space="default">
                  <AggregatedRatingWidget
                    aggregateRating={aggregateRating}
                    serviceSlug={serviceSlug}
                  />
                </LayoutVerticalSpacer>
              )}
              {dynamicSearchContent && (
                <div data-nosnippet>
                  <DynamicSeoContent content={dynamicSearchContent} searchFilters={searchFilters} />
                </div>
              )}
            </>
          )}
        </LayoutGridItem>
      </LayoutGridContainer>
    </>
  );
};

export { SearchResults };
